import { useState } from "react";
import { Button, Drawer, Hairline, Input, Select } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { SupplierContact, SupplierDTO, SupplierFulfilmentMode } from "@wmart/shared-types";

/**
 * Create / edit a supplier. Fields: name, slug, email, phone, notes, active
 * toggle, plus a repeatable contacts editor (name/role/phone/email per row).
 *
 * Submit: POST /v1/admin/suppliers (create) or PATCH /v1/admin/suppliers/:id
 * (edit). contacts is always sent as the full replacement array. Empty-string
 * optional fields are normalised to undefined/null before send so the Zod
 * email validator on the server doesn't reject "".
 */
export function SupplierEditDrawer({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: SupplierDTO | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify, notifyProblem } = useToast();
  const [slug, setSlug] = useState(supplier?.slug ?? "");
  // Once the user edits the slug by hand (or we're editing an existing
  // supplier), stop auto-deriving it from the name.
  const [slugEdited, setSlugEdited] = useState(supplier != null);
  const [name, setName] = useState(supplier?.name ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [phone, setPhone] = useState(supplier?.phone ?? "");
  const [notes, setNotes] = useState(supplier?.notes ?? "");
  const [active, setActive] = useState(supplier?.active ?? true);
  const [fulfilmentMode, setFulfilmentMode] = useState<SupplierFulfilmentMode>(
    supplier?.fulfilment_mode ?? "depot",
  );
  const [contacts, setContacts] = useState<SupplierContact[]>(
    supplier?.contacts ?? [],
  );
  const [busy, setBusy] = useState(false);

  /** Server requires kebab-case (^[a-z0-9]+(?:-[a-z0-9]+)*$). */
  function slugify(s: string): string {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function onNameChange(value: string) {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
  }

  function addContact() {
    setContacts((prev) => [...prev, { name: "", role: "", phone: "", email: "" }]);
  }

  function removeContact(index: number) {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  }

  function updateContact(
    index: number,
    field: keyof SupplierContact,
    value: string,
  ) {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  }

  /** Normalise contacts before send: empty optional strings → undefined. */
  function buildContactPayload(c: SupplierContact): SupplierContact {
    return {
      name: c.name,
      role: c.role && c.role.trim() ? c.role.trim() : undefined,
      phone: c.phone && c.phone.trim() ? c.phone.trim() : undefined,
      email: c.email && c.email.trim() ? c.email.trim() : undefined,
    };
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        slug: slug.trim(),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        active,
        fulfilment_mode: fulfilmentMode,
        contacts: contacts.map(buildContactPayload),
      };

      if (supplier) {
        await api.patch(`/v1/admin/suppliers/${supplier.id}`, payload);
        notify("success", "Supplier updated");
      } else {
        await api.post("/v1/admin/suppliers", payload);
        notify("success", "Supplier created");
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={supplier ? `Edit ${supplier.name}` : "New supplier"}
    >
      <form onSubmit={save} className="flex flex-col gap-4 p-6">
        <Input
          label="Name"
          required
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
        />
        <Input
          label="Slug"
          required
          value={slug}
          onChange={(e) => {
            setSlugEdited(true);
            setSlug(e.target.value);
          }}
          className="font-mono"
          helper="lowercase + hyphens, e.g. sara-international"
        />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <div className="flex flex-col gap-1">
          <label
            htmlFor="supplier-notes"
            className="text-body-sm font-medium text-ink-700"
          >
            Notes
          </label>
          <textarea
            id="supplier-notes"
            className="rounded-sm border border-ink-200 px-3 py-2 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-2 text-body-sm font-medium text-ink-700">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 rounded-sm border border-ink-300 accent-accent"
          />
          Active
        </label>

        <Select
          label="Fulfilment mode"
          value={fulfilmentMode}
          onChange={(e) =>
            setFulfilmentMode(e.target.value as SupplierFulfilmentMode)
          }
          helper="Depot — WMart receives the stock; Dropship — supplier ships direct to customer"
        >
          <option value="depot">Depot — WMart receives</option>
          <option value="dropship">Dropship — ships direct to customer</option>
        </Select>

        {/* Contacts editor */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-body-sm font-medium text-ink-700">Contacts</span>
            <Button type="button" size="sm" variant="secondary" onClick={addContact}>
              + Add contact
            </Button>
          </div>
          {contacts.map((c, i) => (
            <div
              key={i}
              className="flex flex-col gap-2 rounded-sm border border-ink-100 p-3"
            >
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    label="Contact name"
                    required
                    value={c.name}
                    onChange={(e) => updateContact(i, "name", e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Role"
                    value={c.role ?? ""}
                    onChange={(e) => updateContact(i, "role", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    label="Phone"
                    value={c.phone ?? ""}
                    onChange={(e) => updateContact(i, "phone", e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Email"
                    type="email"
                    value={c.email ?? ""}
                    onChange={(e) => updateContact(i, "email", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={`Remove contact ${c.name || i + 1}`}
                  onClick={() => removeContact(i)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Hairline />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy} loading={busy}>
            Save
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
